import React from 'react';
import classes from './UpdateAppoitnmentModal.module.css';
import Modal from '../../../components/UI/Modals/Modal';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { CustomCalendar } from '../../../components/UI/CustomCalendar/CustomCalendar';
import moment from 'moment/moment';
import {
  getProductTimeSlots,
  ITimeSlotInfo,
  selectProductTimeSlots,
  selectProductTimeSlotsStatus,
  selectTimeSlots,
} from '../../../store/slices/auth';

import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import { IProductDetail, selectProductDetails } from '../../../store/slices/productCategories';
import WithLoader from '../../../components/WithLoader/WithLoader';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  salesOrder: SalesOrderInfo;
  isOpen: boolean;
  onClose: () => void;
  handleUpdateAppointment: (start: Date, end: Date) => void;
  loadingUpdate: boolean;
};

const UpdateAppointmentModal: React.FC<Props> = ({
  salesOrder,
  isOpen,
  onClose,
  handleUpdateAppointment,
  loadingUpdate,
}) => {
  const productDetails: IProductDetail[] = useAppSelector<IProductDetail[]>(selectProductDetails);
  const productTimeSlots: ITimeSlotInfo[] = useAppSelector(selectProductTimeSlots);
  const productTimeSlotsStatus: string = useAppSelector(selectProductTimeSlotsStatus);
  const DATE_FORMAT = 'DD.MM.yyyy';
  const { translations } = useTranslation();
  const timeSlots: ITimeSlotInfo[] = useAppSelector(selectTimeSlots);
  const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
  const [appointmentDate, setAppointmentDate] = React.useState<Date | undefined>();
  const [appointmentEndDate, setAppointmentEndDate] = React.useState<Date | undefined>();
  const product = productDetails.find(
    (product) => product.product_id === salesOrder.product.external_product_id
  );
  const startDate = moment();
  const productId = product?.product_id;
  const hasCalendarForProduct = product?.product_calendar[0]?.product_calendar_items?.length > 0;
  const filterData = hasCalendarForProduct ? productTimeSlots : timeSlots;
  const filteredTimeSlots = React.useMemo(() => {
    return filterData.filter((timeSlot) =>
      moment(timeSlot.start).startOf('day').isSame(moment(selectedDate).startOf('day'))
    );
  }, [filterData, selectedDate]);

  const groupByHour = React.useMemo(() => {
    const grouped: { timeSlots: ITimeSlotInfo[] }[] = [];
    for (let i = 0; i < filteredTimeSlots.length; i += 4) {
      grouped.push({
        timeSlots: filteredTimeSlots.slice(i, i + 4),
      });
    }
    return grouped;
  }, [filteredTimeSlots]);

  const highlightDates = Array.from(
    new Set(filterData.map((timeSlot) => moment(timeSlot.start).format(DATE_FORMAT)))
  );
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const fetchTimeSlots = async () => {
      if (hasCalendarForProduct && productId) {
        for (let i = 0; i < product?.product_calendar[0].date_range_days; i++) {
          const timestamp = startDate.clone().add(i, 'days').format('YYYY-MM-DD');
          await dispatch(
            getProductTimeSlots({
              timeStamp: timestamp,
              product_id: productId,
            })
          );
        }
      }
    };
    fetchTimeSlots();
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={translations('appointment').toString()}
      divider
      containerPadding={'80px 20px 20px 20px'}
      maxWidth={'800px'}
      width={'60%'}
    >
      <WithLoader isLoading={productTimeSlotsStatus === 'pending'}>
        <div className={classes.container}>
          <div className={classes.content}>
            <div className={classes.appointmentContainer}>
              <CustomCalendar
                selectedDate={selectedDate}
                onSelectDay={(date) => setSelectedDate(date)}
                highlightedDates={highlightDates}
                format={DATE_FORMAT}
              />
              <div style={{ minWidth: 40 }} />
              <div className={classes.appointments}>
                {groupByHour.length ? (
                  groupByHour.map((hour, index) => (
                    <div key={index} className={classes.row}>
                      {hour.timeSlots.map((timeSlot) => {
                        const startDate = moment(timeSlot.start);
                        return (
                          <div
                            key={`${index}_${timeSlot.start}`}
                            className={`${classes.timeSlot} ${
                              timeSlot.available < 1 || !moment().isBefore(startDate, 'minutes')
                                ? classes.disabledSlot
                                : startDate.isSame(moment(appointmentDate))
                                ? classes.selectedSlot
                                : ''
                            }`}
                            onClick={() => {
                              if (
                                timeSlot.available > 0 &&
                                moment().isBefore(startDate, 'minutes')
                              ) {
                                setAppointmentDate(timeSlot.start);
                                setAppointmentEndDate(timeSlot.end);
                              }
                            }}
                          >
                            {startDate.format('HH:mm')}
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <p>No visits available!</p>
                )}
              </div>
            </div>
          </div>
          <div className={classes.buttonsContainer}>
            <PrimaryButtonV2
              text={translations('save')}
              containerStyle={{
                margin: 5,
              }}
              buttonStyle={{
                height: 40,
                fontSize: 16,
                borderRadius: 7,
                minWidth: 130,
              }}
              disabled={loadingUpdate || !appointmentDate || !appointmentEndDate}
              onClick={() => {
                if (appointmentDate && appointmentEndDate) {
                  handleUpdateAppointment(appointmentDate, appointmentEndDate);
                }
              }}
            />
            <PrimaryButtonV2
              text={translations('button.cancel')}
              invertedColors
              containerStyle={{
                margin: 5,
              }}
              buttonStyle={{
                height: 40,
                fontSize: 16,
                borderRadius: 7,
                minWidth: 130,
              }}
              onClick={onClose}
            />
          </div>
        </div>
      </WithLoader>
    </Modal>
  );
};

export { UpdateAppointmentModal };
